<script lang="ts" setup>
import {
  onMounted,
  onUnmounted,
  ref,
  toRefs,
} from 'vue';

import { GrammarBadge } from '@nora-monorepo/grammar-ui';

interface TimeAgoBadgeProps {
  dateTime: Date | string;
  short?: boolean;
  updateIntervalInMs?: number;
}

const props = withDefaults(defineProps<TimeAgoBadgeProps>(), {
  short: false,
  updateIntervalInMs: 10000,
});

const interval = ref<ReturnType<typeof setTimeout> | null>(null);
const timeAgo = ref('');
const { dateTime, short } = toRefs(props);

function updateTimeAgo() {
  const date = new Date(dateTime.value);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  let updatedTimeAgo = '';

  if (seconds <= 0) {
    updatedTimeAgo = 'now';
  }
  else if (seconds < 60) {
    updatedTimeAgo += seconds;

    if (short.value) {
      updatedTimeAgo += 's';
    }
    else {
      updatedTimeAgo += ' second';

      if (seconds > 1) {
        updatedTimeAgo += 's';
      }
    }
  }
  else if (seconds < 3600) {
    updatedTimeAgo += `${Math.floor(seconds / 60)}`;

    if (short.value) {
      updatedTimeAgo += 'm';
    }
    else {
      updatedTimeAgo += ' minute';

      if (Math.floor(seconds / 60) > 1) {
        updatedTimeAgo += 's';
      }
    }
  }
  else if (seconds < 86400) {
    updatedTimeAgo += `${Math.floor(seconds / 3600)}`;

    if (short.value) {
      updatedTimeAgo += 'h';
    }
    else {
      updatedTimeAgo += ' hour';

      if (Math.floor(seconds / 3600) > 1) {
        updatedTimeAgo += 's';
      }
    }
  }
  else {
    updatedTimeAgo += `${Math.floor(seconds / 86400)}`;

    if (short.value) {
      updatedTimeAgo += 'd';
    }
    else {
      updatedTimeAgo += ' day';

      if (Math.floor(seconds / 86400) > 1) {
        updatedTimeAgo += 's';
      }
    }
  }

  timeAgo.value = updatedTimeAgo;
}

function renewTimeout() {
  if (interval.value) {
    clearTimeout(interval.value);
  }

  interval.value = setTimeout(() => {
    updateTimeAgo();
    renewTimeout();
  }, props.updateIntervalInMs);
}

onMounted(() => {
  updateTimeAgo();

  renewTimeout();
});

onUnmounted(() => {
  if (interval.value) {
    clearTimeout(interval.value);
  }

  interval.value = null;
});
</script>

<template>
  <GrammarBadge :label="timeAgo" emphasis="no-background" />
</template>
